import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Modal, ModalBody, Spinner } from "reactstrap";
import { ArrowUp, Search } from "lucide-react";
import { toast } from "react-toastify";
import {
	adaptKeywordCandidatesPayload,
	adaptKeywordsPayload,
	adaptProject,
	adaptTopicsPayload,
	aiUsageLabel,
	intentLabel,
} from "./monitoringAdapters";
import {
	bulkKeywordPrompts,
	deployKeywordPrompts,
	generateKeywordPrompts,
	getKeywordPromptCandidates,
	getMonitoringKeywords,
	getMonitoringProject,
	getMonitoringTopics,
	importMonitoringKeywords,
	refreshMonitoringKeywordVolume,
	researchMonitoringKeywords,
} from "../../../services/monitoringApi";

const REFRESH_VOLUME_TITLE =
	"Re-fetch volume and AI volume for every tracked keyword from the current vendor, in one bulk call";

const INTENTS = [
	"informational",
	"commercial",
	"transactional",
	"navigational",
	"local",
];

function formatVolume(n) {
	if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
	const v = Number(n);
	if (v >= 1000) return v.toLocaleString("en-US");
	return v ? String(v) : "—";
}

function formatAiVolume(n) {
	if (n == null || n === "" || Number.isNaN(Number(n))) return "0";
	const v = Number(n);
	if (v >= 1000) return v.toLocaleString("en-US");
	return String(v);
}

function aiUsageTooltip(label, ratio) {
	if (label && ratio != null) {
		const pct = ratio <= 1 ? Math.round(ratio * 100) : Math.round(ratio);
		return `${aiUsageLabel(label)} AI usage (${pct}% of search volume)`;
	}
	if (label) return `${aiUsageLabel(label)} AI usage`;
	return null;
}

function IntentChip({ value }) {
	if (!value) return null;
	const key = String(value).toLowerCase();
	return (
		<span className={`monitoring__intent-chip monitoring__intent-chip--${key}`}>
			{intentLabel(key) || key.charAt(0).toUpperCase() + key.slice(1)}
		</span>
	);
}

function AiUsageChip({ value, ratio }) {
	const key = value ? String(value).toLowerCase() : null;
	const label = aiUsageLabel(value);
	if (!key || !label) {
		return (
			<span className="monitoring__usage-chip monitoring__usage-chip--zero">
				0
			</span>
		);
	}
	const tooltip = aiUsageTooltip(value, ratio);
	return (
		<span
			className={`monitoring__usage-chip monitoring__usage-chip--${key}`}
			title={tooltip || undefined}
		>
			{label}
		</span>
	);
}

function flattenRows(keywords, topics) {
	const byId = new Map(topics.map((t) => [t.topicId, t]));
	return keywords.map((k) => {
		const topicId = k.topicId ?? null;
		const topic = topicId === null ? null : byId.get(topicId) ?? null;
		return {
			keyword: k,
			topicId,
			topicName: topic?.name ?? k.topic ?? "Unassigned",
		};
	});
}

function groupIntoSections(rows, topics) {
	const byId = new Map(topics.map((t) => [t.topicId, t]));
	const sections = new Map();
	for (const r of rows) {
		if (!sections.has(r.topicId)) {
			sections.set(r.topicId, {
				topicId: r.topicId,
				name: r.topicName,
				intent:
					r.topicId === null
						? null
						: byId.get(r.topicId)?.intentPrimary ?? null,
				volume: 0,
				rows: [],
			});
		}
		const s = sections.get(r.topicId);
		s.rows.push(r);
		s.volume += r.keyword.searchVolume || 0;
	}
	return [...sections.values()].sort(
		(a, b) =>
			(a.topicId === null ? 1 : 0) - (b.topicId === null ? 1 : 0) ||
			b.volume - a.volume ||
			a.name.localeCompare(b.name)
	);
}

function promptsForKeyword(keyword, candByKeyword) {
	const active = (keyword.prompts || []).map((p) => ({
		key: `active-${p.promptId}`,
		promptId: p.promptId,
		promptText: p.promptText,
		isBranded: p.isBranded,
		promptType: null,
		intent: null,
		journeyStage: null,
		state: "active",
		cited: p.cited,
		tested: p.tested,
	}));
	const candidate = (candByKeyword.get(keyword.keywordId) ?? []).map((p) => ({
		key: `cand-${p.promptId}`,
		promptId: p.promptId,
		promptText: p.promptText,
		isBranded: p.isBranded,
		promptType: p.promptType,
		intent: p.intent,
		journeyStage: p.journeyStage,
		state: "candidate",
	}));
	return [...active, ...candidate];
}

function KeywordPromptsModal({
	isOpen,
	keyword,
	items,
	trackPending,
	generating,
	onClose,
	onTrack,
	onReject,
}) {
	const pendingIds = items
		.filter((it) => it.state === "candidate")
		.map((it) => it.promptId);
	const pendingCount = pendingIds.length;

	return (
		<Modal
			isOpen={isOpen}
			toggle={onClose}
			size="xl"
			modalClassName="monitoring__pr-prompts-modal-overlay"
			className="monitoring__pr-prompts-modal"
			centered
		>
			<ModalBody className="monitoring__pr-prompts-body">
				<div className="monitoring__pr-prompts-header">
					<div className="monitoring__pr-prompts-header-copy">
						<h3 className="monitoring__pr-prompts-title">
							Prompts for &ldquo;{keyword}&rdquo;
						</h3>
						<p className="monitoring__pr-prompts-sub text-muted mb-0">
							{items.length} prompt{items.length === 1 ? "" : "s"}
							{pendingCount > 0 && (
								<>
									{" "}
									·{" "}
									<span className="monitoring__pr-prompts-pending-count">
										{pendingCount} pending
									</span>
								</>
							)}
							{pendingCount > 0
								? " — track to make them live immediately."
								: " — active prompts show citation status after the next run."}
						</p>
					</div>
					<Button
						type="button"
						size="sm"
						color="secondary"
						outline
						className="monitoring__pr-prompts-close"
						onClick={onClose}
						aria-label="Close"
					>
						Close
					</Button>
				</div>

				{pendingCount > 0 && (
					<div className="monitoring__pr-prompts-toolbar">
						<Button
							size="sm"
							color="danger"
							outline
							disabled={trackPending || generating}
							onClick={() => onReject(pendingIds)}
						>
							Discard all pending
						</Button>
					</div>
				)}

				{generating && (
					<div className="monitoring__pr-prompts-generating" role="status">
						<Spinner size="sm" className="me-2" />
						Generating prompts…
					</div>
				)}

				{items.length === 0 ? (
					<p className="monitoring__pr-empty text-muted text-center py-4 mb-0">
						No prompts yet — Generate new prompts for this keyword first.
					</p>
				) : (
					<ul className="monitoring__pr-prompts-list list-unstyled mb-0">
						{items.map((it) => (
							<li key={it.key} className="monitoring__pr-prompt-row">
								<div className="monitoring__pr-prompt-copy">
									<div className="monitoring__pr-prompt-text">{it.promptText}</div>
									{(it.promptType || it.intent || it.isBranded || it.journeyStage) && (
										<div className="monitoring__pr-prompt-tags">
											{it.promptType && (
												<span className="monitoring__pr-tag">{it.promptType}</span>
											)}
											{it.intent && (
												<span className="monitoring__pr-tag">{it.intent}</span>
											)}
											{it.isBranded && (
												<span className="monitoring__pr-tag monitoring__pr-tag--branded">
													branded
												</span>
											)}
											{it.journeyStage && (
												<span className="monitoring__pr-tag">
													{String(it.journeyStage).replace(/_/g, " ")}
												</span>
											)}
										</div>
									)}
								</div>
								<div className="monitoring__pr-prompt-actions">
									{it.state === "active" ? (
										<span
											className={`monitoring__pr-status monitoring__pr-status--${
												it.cited
													? "cited"
													: it.tested
														? "tested"
														: "pending-run"
											}`}
										>
											{it.cited
												? "Cited"
												: it.tested
													? "Not cited"
													: "Not run yet"}
										</span>
									) : (
										<>
											<button
												type="button"
												className="monitoring__pr-row-btn monitoring__pr-row-btn--primary"
												disabled={trackPending || generating}
												onClick={() => onTrack([it.promptId])}
											>
												Track
											</button>
											<Button
												size="sm"
												color="danger"
												outline
												disabled={trackPending || generating}
												onClick={() => onReject([it.promptId])}
											>
												Reject
											</Button>
										</>
									)}
								</div>
							</li>
						))}
					</ul>
				)}
			</ModalBody>
		</Modal>
	);
}

function Th({ children, className = "" }) {
	return (
		<th className={`monitoring__pr-th ${className}`.trim()}>{children}</th>
	);
}

export default function MonitoringPromptResearch({ websiteId }) {
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [importBusy, setImportBusy] = useState(false);
	const [researchBusy, setResearchBusy] = useState(false);
	const [refreshVolumeBusy, setRefreshVolumeBusy] = useState(false);
	const [generatingIds, setGeneratingIds] = useState(() => new Set());
	const [msg, setMsg] = useState(null);
	const [subTab, setSubTab] = useState("recommended");
	const [importText, setImportText] = useState("");
	const [selected, setSelected] = useState(() => new Set());
	const [popupForId, setPopupForId] = useState(null);
	const importCardRef = useRef(null);

	const [topicFilter, setTopicFilter] = useState("");
	const [intentFilter, setIntentFilter] = useState("");
	const [keywordSearch, setKeywordSearch] = useState("");
	const [manualTopicFilter, setManualTopicFilter] = useState("");
	const [manualIntentFilter, setManualIntentFilter] = useState("");
	const [manualKeywordSearch, setManualKeywordSearch] = useState("");

	const [keywordsData, setKeywordsData] = useState(null);
	const [topicsPayload, setTopicsPayload] = useState(null);
	const [candidates, setCandidates] = useState({ total: 0, groups: [] });
	const [project, setProject] = useState(null);

	const loadAll = useCallback(async () => {
		if (websiteId == null || websiteId === "") {
			setLoading(false);
			return;
		}
		setLoading(true);
		const [kwRes, topicsRes, candRes, projectRes] = await Promise.all([
			getMonitoringKeywords(websiteId),
			getMonitoringTopics(websiteId),
			getKeywordPromptCandidates(websiteId, "candidate"),
			getMonitoringProject(websiteId),
		]);

		if (kwRes.ok) {
			setKeywordsData(
				adaptKeywordsPayload({
					keywords: kwRes.keywords,
					groups: kwRes.groups,
					topics: kwRes.topics,
					total: kwRes.total,
					active: kwRes.active,
					cacheFresh: kwRes.cacheFresh,
				})
			);
		} else {
			toast.error(kwRes.message || "Failed to load keywords");
			setKeywordsData(null);
		}

		if (topicsRes.ok) {
			setTopicsPayload(
				adaptTopicsPayload({
					topics: topicsRes.topics,
					unassigned: topicsRes.unassigned,
				})
			);
		} else {
			setTopicsPayload(adaptTopicsPayload({ topics: [], unassigned: null }));
		}

		if (candRes.ok) {
			setCandidates(adaptKeywordCandidatesPayload(candRes));
		} else {
			setCandidates({ total: 0, groups: [] });
		}

		if (projectRes.ok && projectRes.project) {
			setProject(adaptProject(projectRes.project));
		} else {
			setProject(null);
		}

		setLoading(false);
	}, [websiteId]);

	const refreshKeywords = useCallback(async () => {
		const [kwRes, topicsRes] = await Promise.all([
			getMonitoringKeywords(websiteId),
			getMonitoringTopics(websiteId),
		]);
		if (kwRes.ok) {
			setKeywordsData(
				adaptKeywordsPayload({
					keywords: kwRes.keywords,
					groups: kwRes.groups,
					topics: kwRes.topics,
					total: kwRes.total,
					active: kwRes.active,
					cacheFresh: kwRes.cacheFresh,
				})
			);
		}
		if (topicsRes.ok) {
			setTopicsPayload(
				adaptTopicsPayload({
					topics: topicsRes.topics,
					unassigned: topicsRes.unassigned,
				})
			);
		}
	}, [websiteId]);

	const refreshCandidates = useCallback(async () => {
		const candRes = await getKeywordPromptCandidates(websiteId, "candidate");
		if (candRes.ok) {
			setCandidates(adaptKeywordCandidatesPayload(candRes));
		}
	}, [websiteId]);

	useEffect(() => {
		setSubTab("recommended");
		setImportText("");
		setSelected(new Set());
		setPopupForId(null);
		setGeneratingIds(new Set());
		setMsg(null);
		loadAll();
	}, [loadAll]);

	const scrollToImport = useCallback(() => {
		importCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
		importCardRef.current?.querySelector("textarea")?.focus();
	}, []);

	const candByKeyword = useMemo(() => {
		const map = new Map();
		for (const g of candidates.groups || []) {
			for (const p of g.prompts || []) {
				if (p.originKeywordId == null) continue;
				const list = map.get(p.originKeywordId) ?? [];
				list.push(p);
				map.set(p.originKeywordId, list);
			}
		}
		return map;
	}, [candidates]);

	const allCandIds = useMemo(
		() =>
			(candidates.groups || []).flatMap((g) =>
				(g.prompts || []).map((p) => p.promptId)
			),
		[candidates]
	);

	const keywords = keywordsData?.keywords ?? [];
	const topics = topicsPayload?.topics ?? [];

	const recommendedKeywords = useMemo(
		() => keywords.filter((k) => k.source !== "manual"),
		[keywords]
	);
	const manualKeywords = useMemo(
		() => keywords.filter((k) => k.source === "manual"),
		[keywords]
	);

	const es = project?.effectiveSettings ?? {};
	const primary = Number(es.prompts_primary_per_keyword ?? es.promptsPrimaryPerKeyword ?? 1);
	const subq = Number(
		es.prompts_subquestions_per_keyword ?? es.promptsSubquestionsPerKeyword ?? 5
	);

	const toggleOne = useCallback((id) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	async function handleRefreshVolume() {
		if (refreshVolumeBusy || websiteId == null) return;
		setRefreshVolumeBusy(true);
		setMsg(null);
		const res = await refreshMonitoringKeywordVolume(websiteId);
		if (!res.ok) {
			toast.error(res.message || "Could not refresh volume");
			setRefreshVolumeBusy(false);
			return;
		}
		const data = res.data || {};
		const provider = String(data.provider || "unknown");
		if (provider === "none") {
			setMsg("Volume refresh needs a vendor key (provider: none).");
		} else {
			const refreshed = Number(data.refreshed ?? 0);
			const checked = Number(data.checked ?? 0);
			setMsg(
				`Refreshed ${refreshed} of ${checked} keyword(s) from ${provider}.`
			);
		}
		await refreshKeywords();
		setRefreshVolumeBusy(false);
	}

	async function handleFetchBrandKeywords() {
		if (researchBusy || websiteId == null) return;
		setResearchBusy(true);
		setMsg(null);
		const res = await researchMonitoringKeywords(websiteId);
		if (!res.ok) {
			toast.error(res.message || "Could not fetch brand keywords");
			setResearchBusy(false);
			return;
		}
		const data = res.data || {};
		const resultsLen = Array.isArray(data.results) ? data.results.length : null;
		const fetched =
			data.fetched ?? data.fetched_count ?? data.result_count ?? resultsLen;
		toast.success(
			res.message ||
				(fetched != null
					? `Fetched ${fetched} brand keyword${fetched === 1 ? "" : "s"}`
					: "Brand keywords fetched")
		);
		await refreshKeywords();
		setResearchBusy(false);
	}

	async function handleImport() {
		const list = importText
			.split("\n")
			.map((l) => l.trim())
			.filter(Boolean);
		if (!list.length || importBusy) return;
		setImportBusy(true);
		const res = await importMonitoringKeywords(websiteId, {
			keywords: list,
			groupIntoTopics: true,
		});
		setImportBusy(false);
		if (!res.ok) {
			setMsg("Could not add keywords.");
			toast.error(res.message || "Could not add keywords.");
			return;
		}
		const data = res.data || {};
		const added = Number(data.added ?? data.added_count ?? data.imported_count ?? list.length);
		const skipped = Number(data.skipped_duplicates ?? data.skippedDuplicates ?? 0);
		setMsg(
			skipped
				? `Added ${added} keyword(s) — ${skipped} already tracked.`
				: `Added ${added} keyword(s) — see them below to check volume and generate prompts.`
		);
		setImportText("");
		await refreshKeywords();
	}

	async function handleGenerate(keywordIds, { openPopupFor } = {}) {
		const ids = (keywordIds || []).filter((id) => id != null);
		if (!ids.length || generatingIds.size > 0) return;
		setGeneratingIds(new Set(ids));
		if (openPopupFor != null) setPopupForId(openPopupFor);
		const res = await generateKeywordPrompts(websiteId, { keywordIds: ids });
		setGeneratingIds(new Set());
		if (!res.ok) {
			setMsg("Could not generate prompts.");
			toast.error(res.message || "Could not generate prompts.");
			return;
		}
		const data = res.data || {};
		const status = data.status ?? "ok";
		const promptCount = Number(data.prompts ?? data.prompt_count ?? 0);
		if (status !== "ok" || promptCount === 0) {
			setMsg(
				"No prompts generated — try again, or adjust fan-out settings in Settings → Keyword research."
			);
			return;
		}
		const topicCount = Number(data.topics ?? data.topic_count ?? 0);
		const curation = data.curation ?? data.curation_mode ?? null;
		if (curation === "auto") {
			setMsg(
				`Generated ${promptCount} prompt(s) across ${topicCount} topic(s) — already live (auto-curate is on for this project).`
			);
			await refreshKeywords();
		} else {
			setMsg(
				`Generated ${promptCount} prompt(s) — open View prompts to Track the ones you want.`
			);
			await refreshCandidates();
		}
		setSelected(new Set());
	}

	async function handleTrack(promptIds) {
		const ids = (promptIds || []).filter((id) => id != null);
		if (!ids.length || busy) return;
		setBusy(true);
		const res = await deployKeywordPrompts(websiteId, { promptIds: ids });
		setBusy(false);
		if (!res.ok) {
			setMsg("Could not track prompt(s).");
			toast.error(res.message || "Could not track prompt(s).");
			return;
		}
		setMsg(
			`Tracking ${res.deployed || ids.length} prompt(s) — now live in continuous monitoring.`
		);
		await Promise.all([refreshCandidates(), refreshKeywords()]);
	}

	async function handleReject(promptIds) {
		const ids = (promptIds || []).filter((id) => id != null);
		if (!ids.length || busy) return;
		setBusy(true);
		const res = await bulkKeywordPrompts(websiteId, "discard", ids);
		setBusy(false);
		if (!res.ok) {
			setMsg("Could not discard prompt(s).");
			toast.error(res.message || "Could not discard prompt(s).");
			return;
		}
		const updated = Number(
			res.data?.updated ?? res.data?.affected_count ?? ids.length
		);
		setMsg(`Discarded ${updated} prompt(s).`);
		await refreshCandidates();
	}

	if (loading || !keywordsData || !topicsPayload) {
		return (
			<p className="monitoring__empty text-muted mb-0">Loading prompt research…</p>
		);
	}

	const rows = flattenRows(recommendedKeywords, topics);
	const topicOptions = [...topics].sort((a, b) => a.name.localeCompare(b.name));
	const hasUnassigned = rows.some((r) => r.topicId === null);
	const filteredRows = rows.filter(
		(r) =>
			(!topicFilter ||
				(topicFilter === "unassigned"
					? r.topicId === null
					: r.topicId === Number(topicFilter))) &&
			(!intentFilter || r.keyword.intent === intentFilter) &&
			(!keywordSearch.trim() ||
				r.keyword.keyword
					.toLowerCase()
					.includes(keywordSearch.trim().toLowerCase()))
	);
	const sections = groupIntoSections(filteredRows, topics);
	const popupGenerating =
		popupForId != null && generatingIds.has(popupForId);

	const manualRows = flattenRows(manualKeywords, topics);
	const manualHasUnassigned = manualRows.some((r) => r.topicId === null);
	const filteredManualRows = manualRows.filter(
		(r) =>
			(!manualTopicFilter ||
				(manualTopicFilter === "unassigned"
					? r.topicId === null
					: r.topicId === Number(manualTopicFilter))) &&
			(!manualIntentFilter || r.keyword.intent === manualIntentFilter) &&
			(!manualKeywordSearch.trim() ||
				r.keyword.keyword
					.toLowerCase()
					.includes(manualKeywordSearch.trim().toLowerCase()))
	);
	const manualSections = groupIntoSections(filteredManualRows, topics);

	const popupKeyword =
		popupForId != null
			? keywords.find((k) => k.keywordId === popupForId) ?? null
			: null;

	function keywordRow(k, opts = {}) {
		const items = promptsForKeyword(k, candByKeyword);
		const pendingCount = items.filter((it) => it.state === "candidate").length;
		const isGenerating = generatingIds.has(k.keywordId);
		return (
			<tr key={k.keywordId} className="monitoring__pr-data-row">
				{opts.checkbox && (
					<td className="monitoring__pr-check-cell">
						<input
							type="checkbox"
							checked={selected.has(k.keywordId)}
							onChange={() => toggleOne(k.keywordId)}
							aria-label={`Select ${k.keyword}`}
						/>
					</td>
				)}
				<td className="monitoring__pr-keyword-cell">
					{k.keyword}
					{opts.tag && (
						<span className="monitoring__pr-imported-tag">{opts.tag}</span>
					)}
				</td>
				<td className="monitoring__pr-volume-cell text-muted">
					{formatVolume(k.searchVolume)}
				</td>
				<td className="monitoring__pr-ai-volume-cell text-muted">
					{formatAiVolume(k.aiVolume)}
				</td>
				<td className="monitoring__pr-ai-usage-cell">
					<AiUsageChip value={k.aiUsage} ratio={k.aiUsageRatio} />
				</td>
				<td className="monitoring__pr-intent-cell">
					<IntentChip value={k.intent} />
				</td>
				<td className="monitoring__pr-actions-cell">
					<div className="monitoring__pr-row-actions">
						<button
							type="button"
							className="monitoring__pr-table-btn monitoring__pr-table-btn--solid"
							disabled={items.length === 0 || isGenerating}
							onClick={() => setPopupForId(k.keywordId)}
						>
							View prompts
							{items.length > 0 && ` (${items.length})`}
							{pendingCount > 0 && (
								<span className="monitoring__pr-pending-badge">
									{pendingCount} pending
								</span>
							)}
						</button>
						<button
							type="button"
							className="monitoring__pr-table-btn monitoring__pr-table-btn--outline monitoring__pr-generate-btn"
							disabled={isGenerating || generatingIds.size > 0}
							onClick={() =>
								handleGenerate([k.keywordId], { openPopupFor: k.keywordId })
							}
						>
							{isGenerating ? (
								<>
									<Spinner size="sm" className="me-1" />
									Generating…
								</>
							) : (
								"Generate new prompts"
							)}
						</button>
					</div>
				</td>
			</tr>
		);
	}

	function renderTable(sectionsList, { checkbox = false, tag } = {}) {
		if (!sectionsList.length) {
			return (
				<div className="monitoring__pr-empty-card text-muted">
					No keywords match these filters.
				</div>
			);
		}
		return (
			<div className="monitoring__pr-table-wrap">
				<table className="monitoring__table monitoring__pr-table">
					<thead>
						<tr>
							{checkbox && <th className="monitoring__pr-check-cell" />}
							<Th>Keyword</Th>
							<Th>Volume/mo</Th>
							<Th className="monitoring__pr-th--numeric">AI Volume/mo</Th>
							<Th
								className="monitoring__pr-th--hint"
								title="Share of monthly search volume attributed to AI assistants. Hover a badge for the exact ratio."
							>
								AI Usage
							</Th>
							<Th>Intent</Th>
							<th className="monitoring__pr-actions-head" />
						</tr>
					</thead>
					<tbody>
						{sectionsList.map((s) => (
							<Fragment key={s.topicId ?? "unassigned"}>
								<tr
									className={`monitoring__pr-topic-row${
										s.topicId === null
											? " monitoring__pr-topic-row--unassigned"
											: ""
									}`}
								>
									<td colSpan={checkbox ? 7 : 6}>
										<span className="monitoring__pr-topic-label">
											<span className="monitoring__pr-topic-name">{s.name}</span>
											<IntentChip value={s.intent} />
											<span className="monitoring__pr-topic-meta text-muted">
												{s.rows.length} keyword{s.rows.length === 1 ? "" : "s"}{" "}
												· Volume{" "}
												<strong>{formatVolume(s.volume)}</strong>/mo
											</span>
										</span>
									</td>
								</tr>
								{s.rows.map((r) =>
									keywordRow(r.keyword, { checkbox, tag })
								)}
							</Fragment>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	return (
		<div className="monitoring__pr">
			{msg && (
				<div className="monitoring__pr-banner" role="status">
					{msg}
				</div>
			)}

			<div
				className="monitoring__pr-subtabs"
				role="tablist"
				aria-label="Prompt Research view"
			>
				{[
					["recommended", "Recommended Keywords"],
					["research", "Research Keywords"],
				].map(([id, label]) => (
					<button
						key={id}
						type="button"
						role="tab"
						aria-selected={subTab === id}
						className={`monitoring__pr-subtab${
							subTab === id ? " monitoring__pr-subtab--active" : ""
						}`}
						onClick={() => setSubTab(id)}
					>
						{label}
						{id === "research" && manualKeywords.length > 0 && (
							<span className="monitoring__pr-subtab-badge">
								{manualKeywords.length}
							</span>
						)}
					</button>
				))}
			</div>

			{subTab === "research" ? (
				<>
					<div className="monitoring__pr-import-card" ref={importCardRef}>
						<h3 className="monitoring__pr-import-title">Research your keywords</h3>
						<p className="monitoring__pr-import-copy text-muted mb-0">
							Paste your own keyword ideas - one per line - to check their
							volume, then generate prompts for the ones worth tracking.
						</p>
						<Input
							type="textarea"
							rows={4}
							value={importText}
							onChange={(e) => setImportText(e.target.value)}
							placeholder="e.g., shoe store marketing, best running shoes (one per line)"
							className="monitoring__pr-import-textarea"
						/>
						<div className="monitoring__pr-import-actions">
							<button
								type="button"
								className="monitoring__pr-toolbar-btn monitoring__pr-toolbar-btn--primary"
								disabled={!importText.trim() || importBusy}
								onClick={handleImport}
							>
								{importBusy ? (
									<>
										<Spinner size="sm" className="me-1" />
										Analyzing keywords…
									</>
								) : (
									"Research and Add Prompt"
								)}
							</button>
						</div>
					</div>

					{manualKeywords.length === 0 ? (
						<div className="monitoring__pr-empty-card monitoring__pr-empty-card--illustrated text-muted">
							<span className="monitoring__pr-empty-icon" aria-hidden="true">
								<Search size={28} strokeWidth={1.5} />
							</span>
							<p className="monitoring__pr-empty-message mb-2">
								No keywords added yet - paste one or more above to get started.
							</p>
							<button
								type="button"
								className="monitoring__pr-empty-cta"
								onClick={scrollToImport}
							>
								<ArrowUp size={14} aria-hidden="true" />
								Add keywords above
							</button>
						</div>
					) : (
						<>
							<div className="monitoring__pr-filter-bar">
								<Input
									type="search"
									value={manualKeywordSearch}
									onChange={(e) => setManualKeywordSearch(e.target.value)}
									placeholder="Search keywords…"
									aria-label="Search your keywords"
									className="monitoring__pr-filter-input"
								/>
								<Input
									type="select"
									value={manualTopicFilter}
									onChange={(e) => setManualTopicFilter(e.target.value)}
									aria-label="Filter your keywords by topic"
									className="monitoring__pr-filter-select"
								>
									<option value="">All topics</option>
									{topicOptions.map((t) => (
										<option key={t.topicId} value={t.topicId}>
											{t.name}
										</option>
									))}
									{manualHasUnassigned && (
										<option value="unassigned">Unassigned</option>
									)}
								</Input>
								<Input
									type="select"
									value={manualIntentFilter}
									onChange={(e) => setManualIntentFilter(e.target.value)}
									aria-label="Filter your keywords by intent"
									className="monitoring__pr-filter-select"
								>
									<option value="">All intents</option>
									{INTENTS.map((i) => (
										<option key={i} value={i}>
											{i.charAt(0).toUpperCase() + i.slice(1)}
										</option>
									))}
								</Input>
								<button
									type="button"
									className="monitoring__pr-toolbar-btn monitoring__pr-toolbar-btn--outline monitoring__pr-refresh-volume-btn"
									disabled={refreshVolumeBusy}
									title={REFRESH_VOLUME_TITLE}
									onClick={handleRefreshVolume}
								>
									{refreshVolumeBusy ? (
										<>
											<Spinner size="sm" className="me-1" />
											Refreshing…
										</>
									) : (
										"Refresh volume"
									)}
								</button>
							</div>
							{renderTable(manualSections, { tag: "Imported" })}
						</>
					)}
				</>
			) : (
				<>
					<div className="monitoring__pr-header-bar">
						<div>
							<h3 className="monitoring__pr-header-title">Recommended keywords</h3>
							<p className="monitoring__pr-header-meta text-muted mb-0">
								{recommendedKeywords.length} keyword
								{recommendedKeywords.length === 1 ? "" : "s"} across{" "}
								{topics.length} topic{topics.length === 1 ? "" : "s"} · {primary}{" "}
								primary + {subq} sub-question(s) per keyword
							</p>
						</div>
						<div className="monitoring__pr-header-actions">
							<button
								type="button"
								className="monitoring__pr-toolbar-btn monitoring__pr-toolbar-btn--outline"
								disabled={refreshVolumeBusy || busy}
								title={REFRESH_VOLUME_TITLE}
								onClick={handleRefreshVolume}
							>
								{refreshVolumeBusy ? (
									<>
										<Spinner size="sm" className="me-1" />
										Refreshing…
									</>
								) : (
									"Refresh volume"
								)}
							</button>
							<button
								type="button"
								className="monitoring__pr-toolbar-btn monitoring__pr-toolbar-btn--primary"
								disabled={researchBusy || busy}
								onClick={handleFetchBrandKeywords}
							>
								{researchBusy ? (
									<>
										<Spinner size="sm" className="me-1" />
										Fetching Brand Keywords…
									</>
								) : (
									"Fetch Brand Keywords"
								)}
							</button>
						</div>
					</div>

					{rows.length === 0 ? (
						<div className="monitoring__pr-empty-card text-muted">
							No recommended keywords yet — add your own in the{" "}
							<button
								type="button"
								className="monitoring__pr-inline-link"
								onClick={() => setSubTab("research")}
							>
								Research Keywords
							</button>{" "}
							tab, then generate prompts from the table below.
						</div>
					) : (
						<>
							<div className="monitoring__pr-filter-bar monitoring__pr-filter-bar--bulk">
								<Input
									type="search"
									value={keywordSearch}
									onChange={(e) => setKeywordSearch(e.target.value)}
									placeholder="Search keywords…"
									aria-label="Search keywords"
									className="monitoring__pr-filter-input"
								/>
								<Input
									type="select"
									value={topicFilter}
									onChange={(e) => setTopicFilter(e.target.value)}
									aria-label="Filter by topic"
									className="monitoring__pr-filter-select"
								>
									<option value="">All topics</option>
									{topicOptions.map((t) => (
										<option key={t.topicId} value={t.topicId}>
											{t.name}
										</option>
									))}
									{hasUnassigned && (
										<option value="unassigned">Unassigned</option>
									)}
								</Input>
								<Input
									type="select"
									value={intentFilter}
									onChange={(e) => setIntentFilter(e.target.value)}
									aria-label="Filter by intent"
									className="monitoring__pr-filter-select"
								>
									<option value="">All intents</option>
									{INTENTS.map((i) => (
										<option key={i} value={i}>
											{i.charAt(0).toUpperCase() + i.slice(1)}
										</option>
									))}
								</Input>
								{selected.size > 0 && (
									<>
										<span className="monitoring__pr-selection-meta text-muted">
											<strong>{selected.size}</strong> selected
										</span>
										<button
											type="button"
											className="monitoring__pr-clear-link"
											onClick={() => setSelected(new Set())}
										>
											Clear
										</button>
									</>
								)}
								<div className="monitoring__pr-bulk-actions">
									{allCandIds.length > 0 && (
										<Button
											size="sm"
											color="danger"
											outline
											disabled={busy}
											onClick={() => handleReject(allCandIds)}
										>
											Discard all pending
										</Button>
									)}
									<button
										type="button"
										className="monitoring__pr-toolbar-btn monitoring__pr-toolbar-btn--outline"
										disabled={selected.size === 0 || generatingIds.size > 0}
										onClick={() => handleGenerate([...selected])}
									>
										{generatingIds.size > 0 && selected.size > 0 ? (
											<>
												<Spinner size="sm" className="me-1" />
												Generating…
											</>
										) : (
											`Generate for selected (${selected.size}) →`
										)}
									</button>
								</div>
							</div>
							{renderTable(sections, { checkbox: true })}
						</>
					)}

					<p className="monitoring__pr-footnote text-muted">
						<strong>Topic → Keyword → Prompt.</strong> Select keywords for a bulk
						generate, or use a row&apos;s buttons for one keyword. Track a prompt
						in the popup to send it straight into continuous monitoring — no
						separate review step.
					</p>
				</>
			)}

			<KeywordPromptsModal
				isOpen={Boolean(popupKeyword)}
				keyword={popupKeyword?.keyword ?? ""}
				items={
					popupKeyword
						? promptsForKeyword(popupKeyword, candByKeyword)
						: []
				}
				trackPending={busy}
				generating={popupGenerating}
				onClose={() => setPopupForId(null)}
				onTrack={handleTrack}
				onReject={handleReject}
			/>
		</div>
	);
}
